/**
 * DropdownExamples.tsx
 * Comprehensive examples of HeroUI Dropdown component
 * 
 * Showcases all HeroUI Dropdown features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { 
  Dropdown, 
  DropdownTrigger, 
  DropdownMenu, 
  DropdownSection, 
  DropdownItem 
} from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  ChevronDown,
  Settings, 
  User, 
  CreditCard,
  HelpCircle,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Copy,
  Share,
  Download,
  Archive,
  Star,
  Heart,
  Bookmark,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Mail,
  Phone,
  FileText,
  Image as ImageIcon,
  Video
} from 'lucide-react';

interface ActionOption {
  key: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  shortcut?: string;
}

export const DropdownExamples: React.FC = () => {
  const [lastAction, setLastAction] = useState<string>('');
  
  const fileActions: ActionOption[] = [
    { key: 'new', label: 'New File', icon: <Plus size={16} />, shortcut: '⌘N' },
    { key: 'open', label: 'Open File', icon: <FileText size={16} />, shortcut: '⌘O' },
    { key: 'save', label: 'Save', icon: <Download size={16} />, shortcut: '⌘S' },
    { key: 'copy', label: 'Copy', icon: <Copy size={16} />, shortcut: '⌘C' },
    { key: 'share', label: 'Share', icon: <Share size={16} /> },
    { key: 'archive', label: 'Archive', icon: <Archive size={16} /> },
    { key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, color: 'danger' as const }
  ];


  const handleAction = (key: React.Key) => {
    setLastAction(String(key));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Dropdown Examples</h1>
          <p className="text-text-secondary">
            Interactive dropdown menus with actions, sections, and keyboard navigation
          </p>
        </div>

        {/* Status Display */}
        {lastAction && (
          <Card className="mb-6 bg-primary-50 border-primary-200">
            <CardBody>
              <p className="text-primary-800 font-medium">
                Last action triggered: <Badge color="primary" variant="flat">{lastAction}</Badge>
              </p>
            </CardBody>
          </Card>
        )}

        {/* Basic Dropdown Examples */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Basic Dropdowns</h2>
              <Badge color="primary" variant="flat" size="sm">Static & Dynamic</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Static Dropdown */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Static Actions</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button variant="bordered" endContent={<ChevronDown size={16} />}>
                      Actions
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Static Actions" onAction={handleAction}>
                    <DropdownItem key="new">New file</DropdownItem>
                    <DropdownItem key="copy">Copy link</DropdownItem>
                    <DropdownItem key="edit">Edit file</DropdownItem>
                    <DropdownItem key="delete" className="text-danger" color="danger">
                      Delete file
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Dynamic Dropdown */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Dynamic Actions with Icons</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button color="primary" endContent={<ChevronDown size={16} />}>
                      File Menu
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    items={fileActions}
                    aria-label="Dynamic file actions"
                    onAction={handleAction}
                  >
                    {(item) => (
                      <DropdownItem
                        key={item.key}
                        color={item.color}
                        startContent={item.icon}
                        description={item.shortcut}
                      >
                        {item.label}
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Sectioned Dropdowns */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Dropdown Sections</h2>
              <Badge color="secondary" variant="flat" size="sm">Organized groups</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Menu with Sections */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">User Account Menu</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button variant="bordered" startContent={<User size={16} />} endContent={<ChevronDown size={16} />}>
                      User Menu
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="User actions with sections"
                    onAction={handleAction}
                  >
                    <DropdownSection title="Account">
                      <DropdownItem key="profile" startContent={<User size={16} />}>
                        Profile
                      </DropdownItem>
                      <DropdownItem key="billing" startContent={<CreditCard size={16} />}>
                        Billing
                      </DropdownItem>
                    </DropdownSection>
                    <DropdownSection title="Preferences">
                      <DropdownItem key="settings" startContent={<Settings size={16} />}>
                        Settings
                      </DropdownItem>
                      <DropdownItem key="help" startContent={<HelpCircle size={16} />}>
                        Help & Support
                      </DropdownItem>
                    </DropdownSection>
                    <DropdownSection title="Danger Zone">
                      <DropdownItem key="logout" color="danger" startContent={<LogOut size={16} />}>
                        Sign Out
                      </DropdownItem>
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Creation Menu */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Complex Creation Menu</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button 
                      color="primary" 
                      variant="shadow"
                      startContent={<Plus size={18} />}
                      endContent={<ChevronDown size={18} />}
                    >
                      Create New
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Advanced creation menu"
                    onAction={handleAction}
                  >
                    <DropdownSection title="Documents">
                      <DropdownItem key="text-doc" startContent={<FileText size={16} />}>
                        Text Document
                      </DropdownItem>
                      <DropdownItem key="spreadsheet" startContent={<FileText size={16} />}>
                        Spreadsheet
                      </DropdownItem>
                    </DropdownSection>
                    <DropdownSection title="Media">
                      <DropdownItem key="image" startContent={<ImageIcon size={16} />}>
                        Image Gallery
                      </DropdownItem>
                      <DropdownItem key="video" startContent={<Video size={16} />}>
                        Video Project
                      </DropdownItem>
                    </DropdownSection>
                    <DropdownSection title="Communication">
                      <DropdownItem key="email" startContent={<Mail size={16} />}>
                        Email Campaign
                      </DropdownItem>
                      <DropdownItem key="call" startContent={<Phone size={16} />}>
                        Voice Call
                      </DropdownItem>
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Dropdown Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Button Variants</h2>
              <Badge color="warning" variant="flat" size="sm">7 variants</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-4">
              {(['solid', 'bordered', 'light', 'flat', 'faded', 'shadow', 'ghost'] as const).map((variant) => (
                <Dropdown key={variant}>
                  <DropdownTrigger>
                    <Button variant={variant} endContent={<ChevronDown size={16} />}>
                      {variant}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label={`${variant} dropdown`} onAction={handleAction}>
                    <DropdownItem key={`${variant}-1`}>Action 1</DropdownItem>
                    <DropdownItem key={`${variant}-2`}>Action 2</DropdownItem>
                    <DropdownItem key={`${variant}-3`}>Action 3</DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Special Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Special Features</h2>
              <Badge color="success" variant="flat" size="sm">Disabled items & Selection</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Disabled Items */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Disabled Items</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button variant="flat" endContent={<ChevronDown size={16} />}>
                      With Disabled
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Actions with disabled items"
                    disabledKeys={["edit", "archive"]}
                    onAction={handleAction}
                  >
                    <DropdownItem key="view" startContent={<Eye size={16} />}>
                      View
                    </DropdownItem>
                    <DropdownItem key="edit" startContent={<Edit size={16} />}>
                      Edit (Disabled)
                    </DropdownItem>
                    <DropdownItem key="copy" startContent={<Copy size={16} />}>
                      Copy
                    </DropdownItem>
                    <DropdownItem key="archive" startContent={<Archive size={16} />}>
                      Archive (Disabled)
                    </DropdownItem>
                    <DropdownItem key="delete" color="danger" startContent={<Trash2 size={16} />}>
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Single Selection */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Single Selection</h3>
                <Dropdown>
                  <DropdownTrigger>
                    <Button color="success" variant="ghost" endContent={<ChevronDown size={16} />}>
                      Visibility
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Single selection dropdown"
                    selectionMode="single"
                    onAction={handleAction}
                  >
                    <DropdownItem key="public" startContent={<Unlock size={16} />}>
                      Public
                    </DropdownItem>
                    <DropdownItem key="private" startContent={<Lock size={16} />}>
                      Private
                    </DropdownItem>
                    <DropdownItem key="hidden" startContent={<EyeOff size={16} />}>
                      Hidden
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Stay Open (Multi-select) */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Stay Open</h3>
                <Dropdown closeOnSelect={false}>
                  <DropdownTrigger>
                    <Button variant="bordered" color="secondary" endContent={<ChevronDown size={16} />}>
                      Multi-Select
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Multi-select dropdown"
                    selectionMode="multiple"
                    onAction={handleAction}
                  >
                    <DropdownItem key="notifications" startContent={<Star size={16} />}>
                      Notifications
                    </DropdownItem>
                    <DropdownItem key="bookmarks" startContent={<Bookmark size={16} />}>
                      Bookmarks
                    </DropdownItem>
                    <DropdownItem key="favorites" startContent={<Heart size={16} />}>
                      Favorites
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};

export default DropdownExamples;